import { FC, useRef, useState } from 'react';
import { useModal } from 'store/modal';
import { FaPen } from 'react-icons/fa';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { useWorkModeContext } from 'store/work-mode';
import { sendToEmployer, signDocAsEmployer } from 'routes/client/documents/helpers';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import setServerErrors from 'utils/helpers/formErrors';
import { useTwoFactorContext } from 'store/two-factor';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Spinner from 'components/Spinner';
import { HandleEmployeeSignOptions } from 'components/form/CodeForm';
import handleEmployeeCode from 'utils/helpers/handleEmployeeCode';
import useInvalidateQueries from 'utils/helpers/useInvalidateQueries';

interface IProps {
  id: string;
  docHolderId: string;
  buttonText: string;
  handlePreview: (docId: string) => void;
  invalidate: () => void;
  size?: 'sm' | 'lg';
  ariaLabel?: string;
  showExitInterview?: boolean;
  exitInterviewUrl?: string;
}

type SignDocFlowOptions = Pick<IProps, 'invalidate' | 'showExitInterview' | 'exitInterviewUrl'>;

export const useSignDocFlow = ({ invalidate, showExitInterview, exitInterviewUrl }: SignDocFlowOptions) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const { is2FaEnabled } = useTwoFactorContext();
  const { workMode } = useWorkModeContext();
  const isEmployer = workMode === 'employer';
  const [isLoading, setIsLoading] = useState(false);
  const invalidateQueries = useInvalidateQueries();

  const exitInterviewRef = useRef(false);

  const setExitInterviewRef = (toggle: boolean) => {
    exitInterviewRef.current = toggle;
  };

  const handleEmployerSign = (docId: string, docHolderId: string) => {
    showModal({
      type: 'loading',
      heading: t('documents.eSign'),
      content: t('documents.eSignSubtitle'),
    });
    signDocAsEmployer(
      docId,
      docHolderId,
      showModal,
      hideModal,
      () => invalidateQueries([], invalidate),
      'documents.table.signedDocSuccessMsg',
      t
    );
  };

  const handleEmployeeSign = (options: HandleEmployeeSignOptions, docHolderId: string) => {
    const { docId, data, methodsProp, extraAction, extraActionCB } = options;
    const dataCopy = { ...data };
    if (extraAction) {
      dataCopy.exit_interview_opened = true;
    }
    return API.post(`documents/${docId}/sign-ades`, dataCopy)
      .then(res => {
        const { message } = res.data;
        showModal({
          type: 'success',
          content: t('documents.table.signedDocSuccessMsg'),
          onSuccess: () => {
            if (message?.type === 'send_to_employer')
              sendToEmployer(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);
          },
        });

        invalidateQueries([], invalidate);

        if (extraActionCB) {
          extraActionCB();
        }
      })
      .catch(response => {
        const error = createErrorFromResponse(response);
        if (methodsProp) {
          setServerErrors(error.errors, methodsProp.setError);
        } else {
          showModal({
            type: 'error',
            content: createErrorFromResponse(error).message,
          });
        }
      });
  };

  const openSignConfirm = (docId: string, docHolderId: string = docId) => {
    showModal({
      type: 'confirm',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('documents.table.confirmSignDocMsg')}</p>
          <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => handlePreviewDoc({ showModal, id: docId, setIsLoading })}
          >
            {!isLoading && t('documents.table.previewDoc')}

            {isLoading && <Spinner size={14} />}
          </Button>
          <hr />
        </>
      ),
      onConfirm: () => {
        if (isEmployer) {
          handleEmployerSign(docId, docHolderId);
        } else {
          return is2FaEnabled
            ? handleEmployeeCode({
                docId,
                showModal,
                heading: t('documents.table.signWithCodeLabel'),
                handleEmployeeSign: options => handleEmployeeSign(options, docHolderId),
                extraAction: showExitInterview,
                setExitInterviewRef,
                extraActionCB: () => {
                  if (showExitInterview && exitInterviewRef.current) {
                    exitInterviewRef.current = false;
                    setTimeout(() => {
                      window.open(exitInterviewUrl, '_blank');
                    });
                  }
                },
              })
            : handleEmployeeSign({ docId, data: {} }, docHolderId);
        }
      },
      confirmLabel: t('documents.table.confirmSignDocLabel'),
    });
  };

  return { openSignConfirm };
};

const SignDoc: FC<IProps> = ({
  id,
  docHolderId,
  buttonText,
  invalidate,
  size = 'sm',
  showExitInterview,
  exitInterviewUrl,
}: IProps) => {
  const { openSignConfirm } = useSignDocFlow({ invalidate, showExitInterview, exitInterviewUrl });

  return (
    <div className="d-inline-block ml-2 mb-2 mb-md-0">
      <TooltipWrapper
        id="tooltip-sign"
        overlay={buttonText}
      >
        <Button
          ariaLabel="Sign document"
          type="button"
          btnType="info"
          size={size}
          onClick={() => openSignConfirm(id, docHolderId)}
        >
          <FaPen size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default SignDoc;
